import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { authService } from '../services/authService';
import { recommendationService } from '../services/recommendationService';
import RecommendationCard from '../components/RecommendationCard';
import { tokens } from '../theme/tokens';

/**
 * Recommendations Page
 * Displays security recommendations grouped by exposure type,
 * each with actionable steps and links to affected posts.
 */
const RecommendationsPage = () => {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRecommendations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRecommendations = async () => {
    setLoading(true);
    setError(null);
    try {
      const currentUser = authService.getStoredUser();
      if (!currentUser) {
        navigate('/login');
        return;
      }
      const username = currentUser.reddit_username || currentUser.username;
      const platform = currentUser.active_platform || 'reddit';
      const data = await recommendationService.getRecommendations(username, platform);
      setRecommendations(data);
    } catch (err) {
      console.error('Failed to load recommendations:', err);
      setError('Failed to load recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, pb: 6 }}>
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: `${tokens.radius.card}px`,
          border: tokens.hero.border,
          boxShadow: tokens.hero.shadow,
          background: tokens.hero.gradient,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: 'rgba(255, 255, 255, 0.12)',
                display: 'flex',
              }}
            >
              <SecurityIcon sx={{ fontSize: 32, color: tokens.text.primary }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', color: tokens.text.primary }}>
              Security Recommendations
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadRecommendations}
              disabled={loading}
              sx={{
                minHeight: 44,
                color: tokens.text.primary,
                borderColor: tokens.border.strong,
                '&:hover': {
                  borderColor: tokens.accent.cyan,
                  backgroundColor: 'rgba(34, 211, 238, 0.08)',
                },
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/dashboard')}
              sx={{
                minHeight: 44,
                bgcolor: tokens.accent.brandTeal,
                color: '#05201A',
                boxShadow: '0 6px 16px rgba(24, 184, 154, 0.35)',
                '&:hover': { bgcolor: '#14A88D' },
              }}
            >
              Back to Dashboard
            </Button>
          </Box>
        </Box>
        <Typography variant="body1" sx={{ color: tokens.text.secondary }}>
          Based on your unresolved privacy exposures, here are the steps you should take to protect your data.
        </Typography>
      </Box>

      <Divider sx={{ mb: 3, borderColor: tokens.border.subtle }} />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={52} thickness={4} sx={{ color: 'primary.main' }} />
        </Box>
      )}

      {/* Error state */}
      {!loading && error && (
        <Alert
          severity="error"
          variant="outlined"
          sx={{
            mb: 2,
            bgcolor: 'rgba(239, 68, 68, 0.08)',
            borderColor: 'rgba(239, 68, 68, 0.35)',
            color: tokens.accent.danger,
            '& .MuiAlert-icon': { color: tokens.accent.danger },
          }}
        >
          {error}
        </Alert>
      )}

      {/* Empty state */}
      {!loading && !error && recommendations.length === 0 && (
        <Alert
          severity="success"
          variant="outlined"
          sx={{
            mb: 2,
            bgcolor: 'rgba(34, 197, 94, 0.08)',
            borderColor: 'rgba(34, 197, 94, 0.35)',
            color: tokens.text.primary,
            '& .MuiAlert-icon': { color: tokens.accent.safe },
          }}
        >
          <Typography variant="body1" fontWeight={700} sx={{ color: tokens.text.primary }}>
            No active recommendations
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.75, color: tokens.text.muted }}>
            Either you have no exposures yet, or all exposures have been resolved. Run a new scan to check for new exposures.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/dashboard')}
              sx={{
                color: tokens.text.primary,
                borderColor: tokens.border.strong,
                '&:hover': { borderColor: tokens.accent.cyan, bgcolor: 'rgba(34, 211, 238, 0.08)' },
              }}
            >
              Run New Scan
            </Button>
          </Box>
        </Alert>
      )}

      {/* Recommendations list */}
      {!loading && !error && recommendations.length > 0 && (
        <>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
            {recommendations.length} recommendation{recommendations.length !== 1 ? 's' : ''} — address high priority items first
          </Typography>
          {recommendations.map((rec) => (
            <RecommendationCard key={rec.id} recommendation={rec} />
          ))}
          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/exposures')}
              sx={{
                color: tokens.text.primary,
                borderColor: tokens.border.strong,
                fontWeight: 600,
                '&:hover': { borderColor: tokens.accent.cyan, bgcolor: 'rgba(34, 211, 238, 0.08)' },
              }}
            >
              View All Exposures
            </Button>
          </Box>
        </>
      )}
    </Container>
  );
};

export default RecommendationsPage;
